import { Link } from "react-router";

import { Caution, HelpLayout, HelpSectionBlock } from "@/components/help-layout";
import { Notice } from "@/components/page";
import { subText, table, td, th } from "@/components/table";
import { useMe, useWorkforceVocab } from "@/lib/api";

/**
 * How the Workforce page works, written for the HR Business Partner who has
 * just been told "it is in HROps now" and wants to know what that means for
 * the form they used to fill in.
 *
 * Same rules as /help: say what the system does, name the consequences, and
 * never describe how the backend is configured. Lists that can change
 * (entities, countries, areas) are read live from the same vocabulary the form
 * uses, so this page cannot drift from the dropdowns.
 */

const SECTIONS: Array<{ id: string; title: string }> = [
  { id: "what", title: "What this replaces" },
  { id: "who", title: "Roles: who can do what" },
  { id: "situations", title: "Which form for which situation" },
  { id: "forms", title: "The three forms and their fields" },
  { id: "approval", title: "What approval does" },
  { id: "hire", title: "New hires and the IT helpdesk" },
  { id: "exit", title: "Exits and the offboarding" },
  { id: "org", title: "Org-data changes and the directory" },
  { id: "pay", title: "Compensation and who sees it" },
  { id: "fhid", title: "Future Hire IDs" },
  { id: "mail", title: "Mails HROps sends" },
  { id: "log", title: "The log and the Excel export" },
  { id: "lists", title: "The dropdown lists" },
];

export function WorkforceHelpPage() {
  const me = useMe().data;
  const vocab = useWorkforceVocab(me?.workforce_enabled === true).data;
  const helpdesk = vocab?.helpdesk_address ?? "Global IT Helpdesk";

  return (
    <HelpLayout
      title="Workforce"
      sections={SECTIONS}
      intro={
        <>
          The Norican Org Workforce Change Input Form, inside HROps: hires, exits, planned exits,
          future positions and changes to somebody&apos;s supervisor, job, location or legal entity,
          with an approval step. This page explains the concepts, the data and what happens after a
          click.{" "}
          <Link className="underline" to="/workforce">
            Go to the Workforce page
          </Link>
        </>
      }
    >
      <HelpSectionBlock id="what" title="What this replaces">
        <p>
          Until October 2026 the form was an Excel workbook with four tabs (START HERE, EMPLOYEE
          CHANGE, PLANNED EXIT, FUTURE HIRE), mailed around and stored wherever it landed. The same
          form now lives on the Workforce page. Nothing is stored in a SharePoint folder; every
          submission is a record in HROps with a status, an approver and a log.
        </p>
        <p>
          Three things are different from the workbook. Free-text fields for legal entity, country,
          area and currency are dropdowns from one list. Submitting sends the form to an approver,
          who accepts it or sends it back. And an approval does things: it raises the offboarding,
          writes the org data to the directory, or hands a hire to IT, instead of leaving that for
          somebody to do by hand later.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="who" title="Roles: who can do what">
        <p>
          Access to HROps is a role on the HROps application in Entra. Three exist; the Workforce
          page is opened by one of them and has an approver list inside it.
        </p>
        <table className={`${table} mt-3`}>
          <thead>
            <tr>
              <th className={th}>Who</th>
              <th className={th}>On the Workforce page</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={td}>
                <strong>HROps.Workforce</strong>
                <div className={subText}>the Workforce role</div>
              </td>
              <td className={td}>
                Sees the Workforce menu. Reports any change, sees every request and the log, edits
                or withdraws own requests while they are open, downloads the Excel export (without
                pay columns). Sees compensation only on records they submitted or are the
                responsible HRBP for.
              </td>
            </tr>
            <tr>
              <td className={td}>
                <strong>Approver</strong>
                <div className={subText}>a named list inside the page, not a role</div>
              </td>
              <td className={td}>
                A Workforce role holder who additionally approves or sends back any request, sees
                compensation on every record, and gets the pay columns in the export. Today: the HR
                owner of the workforce process and IT.
              </td>
            </tr>
            <tr>
              <td className={td}>
                <strong>HROps.User</strong> and <strong>HROps.Admin</strong>
                <div className={subText}>the two roles everybody else holds</div>
              </td>
              <td className={td}>
                Do not see the Workforce page at all. They run the rest of HROps: leavers, risk
                flags, access lists, the org chart. An approved exit from here lands in their queue
                like any other offboarding.
              </td>
            </tr>
          </tbody>
        </table>
        <p className={`${subText} mt-2`}>
          {me?.workforce_approver
            ? "You hold the Workforce role and you are an approver."
            : me?.workforce_enabled
              ? "You hold the Workforce role. You are not an approver."
              : "You do not hold the Workforce role."}{" "}
          Roles are assigned by IT; a role granted while you are signed in is picked up after you
          sign out and back in. The full picture is under{" "}
          <Link className="underline" to="/help/roles">
            Roles and access
          </Link>
          .
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="situations" title="Which form for which situation">
        <p>
          The first dropdown on <em>Report a change</em> is the workbook&apos;s START HERE tab. Pick
          the situation and the right form appears.
        </p>
        <table className={`${table} mt-3`}>
          <thead>
            <tr>
              <th className={th}>Situation</th>
              <th className={th}>Form now</th>
              <th className={th}>Later</th>
            </tr>
          </thead>
          <tbody>
            {(vocab?.situations ?? FALLBACK_SITUATIONS).map((s) => (
              <tr key={s.id}>
                <td className={td}>{s.label}</td>
                <td className={td}>{KIND_LABEL[s.kind]}</td>
                <td className={`${td} text-fg-muted`}>{s.later ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </HelpSectionBlock>

      <HelpSectionBlock id="forms" title="The three forms and their fields">
        <h3 className="text-fg mt-2 text-sm font-semibold">Employee change</h3>
        <p>
          An actual event for a named person: new hire, exit, supervisor change, job or function
          change, transfer, location change, legal entity change, other. Always: change type,
          effective date, name, responsible HRBP. For anyone who already has an account, the person
          is picked from the directory so the change is applied to the right account.
        </p>
        <p>
          <strong>Organisational data</strong> is a Current / New table for supervisor, job title or
          function, department, area, country, city, legal entity and brand. For a new hire,
          complete the New column; supervisor and job title are required, because the Ivanti
          new-user request cannot be filed without them. For any other change, complete only the
          rows that change. Brand is recorded but never written anywhere.
        </p>
        <p>
          Every required field carries a red <span className="text-danger-fg">*</span>. Until all of
          them are filled, the submit button stays grey and the line under it names the ones still
          empty. On a new hire, a planned exit and a future hire the compensation is required: all
          three figures and the currency.
        </p>
        <p>
          <strong>New hire only:</strong> the Future Hire ID this person fills, if the position was
          reported earlier, and the actual annual salary, benefits cost, employer cost and currency.
          Plus the IT set-up the helpdesk needs: the site (primary location), the type of computer,
          whether a mobile phone is needed, and whether the person needs access to a business system
          such as D365, and if so a colleague whose access they should get.
        </p>
        <p>
          <strong>Exit only:</strong> whether the person was on the existing Exit List, the actual
          exit date, the position outcome, and, if they were not on the list, whether they leave
          instead of somebody who was. Plus what HROps needs to raise the offboarding: the time of
          day and zone when access ends, who takes the mailbox (and whether with Send As), who takes
          the OneDrive.
        </p>
        <h3 className="text-fg mt-4 text-sm font-semibold">Planned exit</h3>
        <p>
          An additional planned exit that is not yet on the existing Exit List: name, area, job
          title, country, legal entity, expected exit date, compensation, HRBP. It is a record for
          the exit list, not an offboarding. When the person actually leaves, report the exit as an
          employee change.
        </p>
        <h3 className="text-fg mt-4 text-sm font-semibold">Future hire</h3>
        <p>
          An anonymous future position: area, wave, role, supervisor, country, city, legal entity,
          expected hire quarter, HRBP, and target compensation. HROps assigns the Future Hire ID on
          submission. When somebody is hired for it, report the new hire as an employee change and
          pick that ID.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="approval" title="What approval does">
        <p>
          A submitted request waits for an approver. Approving is final and does the work the record
          implies; sending it back returns it to the submitter with a note, who edits and resubmits.
          The submitter can withdraw a request at any point before approval.
        </p>
        <table className={`${table} mt-3`}>
          <thead>
            <tr>
              <th className={th}>Record</th>
              <th className={th}>On approval</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={td}>Exit</td>
              <td className={td}>
                The offboarding is raised, with the date, time, mailbox and OneDrive handover from
                the form. If an offboarding for that person is already open, the request is linked
                to it instead.
              </td>
            </tr>
            <tr>
              <td className={td}>Supervisor, job, transfer, location, legal entity, other</td>
              <td className={td}>
                The changed fields are written to the person&apos;s account in the directory within
                a few minutes. Fields that already hold the new value are skipped and said so. Brand
                is recorded only.
              </td>
            </tr>
            <tr>
              <td className={td}>New hire</td>
              <td className={td}>
                An &quot;Access - New user&quot; request is filed in Ivanti for Global IT Helpdesk.
                The Future Hire ID, if given, is marked as filled.
              </td>
            </tr>
            <tr>
              <td className={td}>Planned exit, future hire</td>
              <td className={td}>Recorded. Nothing is changed in any system.</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-2">
          Every record shows afterwards what its approval did, with a link to the offboarding record
          or the state of the directory write.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="hire" title="New hires and the IT helpdesk">
        <p>
          HROps creates no accounts. When a new hire is approved, HROps files an{" "}
          <strong>&quot;Access - New user&quot;</strong> request in Ivanti for Global IT Helpdesk,
          with the person who reported the hire as the requester and &quot;HR Portal&quot; as its
          source. It carries the name, start date, job title, supervisor, the IT set-up (site,
          computer, mobile phone, system access and reference user), and in its notes the
          department, area, legal entity, country and city, the HRBP and the approver. From there
          the account, mailbox, licence and equipment are handled by IT exactly as for every joiner.
          The pay figures are never part of it.
        </p>
        <p>
          The record shows the request number (SR …) once Ivanti has it, usually within five
          minutes. Should Ivanti refuse it five times in a row, the request goes to {helpdesk} by
          mail instead, and the record says so.
        </p>
        <Caution>
          Report the hire as early as the start date is confirmed. The request is raised at
          approval, so a hire approved the day before the start date gives IT one day.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="exit" title="Exits and the offboarding">
        <p>
          An approved exit is a normal HROps offboarding, visible in the queue under Leavers. At the
          time named on the form the account is disabled in Entra and in on-prem AD, sessions are
          revoked, devices are disabled, and the mailbox and OneDrive are handed over. Two weeks
          later the mailbox becomes shared and the licence is released; until then the offboarding
          can be reverted from the queue. An exit date that is already past means right away.
        </p>
        <p>
          What the Exit List fields are for: they reconcile the exit with the plan. &quot;On the
          existing Exit List&quot; and &quot;replaces somebody on the list&quot; keep the headcount
          plan and the actual leavers in step. They change nothing about the offboarding itself.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="org" title="Org-data changes and the directory">
        <p>
          Supervisor, job title, department, area, country, city and legal entity are written to the
          person&apos;s account in Active Directory and appear in Entra, Outlook and Teams after the
          next sync, usually within half an hour. The write refuses a value if the directory has
          changed since the request was made (so a parallel edit is never overwritten blindly) and
          refuses protected administrator accounts. Both show up on the record as a skipped field
          with the reason.
        </p>
        <p>
          The Current column on the form is for the approver&apos;s benefit and the record; the
          directory itself is the source of truth for what is written.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="pay" title="Compensation and who sees it">
        <p>
          Salary, benefits cost and employer cost are stored on the record, in their own place, and
          shown only to three kinds of reader: the person who submitted the record, the responsible
          HRBP named on it, and the approvers. Everybody else sees the record without the figures
          and a sentence saying they are withheld. Every time the figures are shown to somebody, a
          log entry is written.
        </p>
        <p>
          The Excel export follows the same rule: approvers get the compensation columns, everybody
          else does not.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="fhid" title="Future Hire IDs">
        <p>
          A future position gets an ID when it is submitted: FH-0001, FH-0002 and so on, assigned
          centrally by HROps. When the position is filled, the new-hire form offers the open IDs in
          a dropdown, and the one picked is marked as filled by that person. That is how target
          compensation (on the future hire) and actual compensation (on the new hire) sit on the
          same position and nothing is counted twice or missed.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="mail" title="Mails HROps sends">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>To the approvers</strong> when a request is submitted or resubmitted, with a
            link to it.
          </li>
          <li>
            <strong>To the submitter and the responsible HRBP</strong> when a request is approved or
            sent back, with the approver&apos;s note and what HROps did.
          </li>
          <li>
            <strong>For {helpdesk}</strong> when an approved exit has been executed: an &quot;Access
            - Leaver&quot; request in Ivanti carrying the usual leaver notice, as for every
            offboarding. Nothing goes to {helpdesk} by mail: Ivanti turns a mail into an incident.
            If Ivanti refuses a request, SecOps sees it in the operations view.
          </li>
        </ul>
        <p className={`${subText} mt-2`}>
          Mails come from HROps&apos; own address within a few minutes of the event. The flagged or
          named person is never told by HROps.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="log" title="The log and the Excel export">
        <p>
          The Log tab lists everything the Workforce page caused, newest first: submissions, edits,
          approvals, returns, withdrawals, every time compensation was viewed, every export, the
          offboardings and directory writes an approval created, and the mails sent. It is the same
          append-only trail every other HROps screen writes; a correction is a new row, never an
          edit. Filter by action or search for a person.
        </p>
        <p>
          <em>Download everything as Excel</em> on the Workforce page produces one workbook with a
          sheet per form, every record with its status, approver and outcome. That is the
          spreadsheet view of the same data, not a separate store.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="lists" title="The dropdown lists">
        <p>
          The lists come from the directory as it is, one spelling per entity, so a change reported
          here matches what the account already holds. A missing entity, country or site is a
          request to IT, not a reason to type something else.
        </p>
        {vocab ? (
          <div className="mt-3 grid gap-6 md:grid-cols-2">
            <ListBlock title="Legal entities" items={vocab.legal_entities} />
            <ListBlock title="Countries" items={vocab.countries} />
            <ListBlock title="Areas (business units)" items={vocab.areas} />
            <ListBlock title="Currencies" items={vocab.currencies} />
            <ListBlock title="Position outcomes" items={vocab.position_outcomes} />
            <ListBlock title="Hire quarters" items={vocab.hire_quarters} />
          </div>
        ) : (
          <Notice>The lists are shown here once you have access to the Workforce page.</Notice>
        )}
      </HelpSectionBlock>
    </HelpLayout>
  );
}

const KIND_LABEL: Record<string, string> = {
  employee_change: "Employee change",
  planned_exit: "Planned exit",
  future_hire: "Future hire",
};

const FALLBACK_SITUATIONS: Array<{
  id: string;
  label: string;
  kind: string;
  later?: string | null;
}> = [
  { id: "new_hire", label: "A new employee joins", kind: "employee_change" },
  {
    id: "exit_listed",
    label: "An employee on the existing Exit List actually leaves",
    kind: "employee_change",
  },
  {
    id: "exit_unlisted",
    label: "An employee who is NOT on the existing Exit List leaves",
    kind: "employee_change",
  },
  {
    id: "exit_replacement",
    label: "A different employee leaves instead of one named on the Exit List",
    kind: "employee_change",
  },
  {
    id: "planned_exit",
    label: "An additional exit is newly planned and not yet on the Exit List",
    kind: "planned_exit",
    later: "Report the exit as an employee change when the person actually leaves.",
  },
  {
    id: "org_change",
    label: "Supervisor, job/function, location or legal entity changes",
    kind: "employee_change",
  },
  {
    id: "future_hire",
    label: "A future position is planned but nobody has been identified yet",
    kind: "future_hire",
    later:
      "Report the new hire as an employee change, with the Future Hire ID, when the person is hired.",
  },
  {
    id: "future_hire_filled",
    label: "A previously reported Future Hire is now filled",
    kind: "employee_change",
  },
];

function ListBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h3 className="text-fg-muted text-xs font-medium">{title}</h3>
      <ul className="mt-1 columns-1 text-sm">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}
